import { Fragment, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { format, parseISO } from 'date-fns';
import { createPopper } from '@popperjs/core';

import Tag from 'components/Tag';
import Divider from 'components/Divider';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { recentUpdateItemIcon } from './Icons';
import { RecentUpdateItem } from 'api/orders/schemas';
import { createPortal } from 'react-dom';
import { CopyOutline } from 'react-ionicons';
import { RecentItemStatus } from '../utils';

const UpdateItem = ({
  item,
  showDivider,
}: {
  item: RecentUpdateItem;
  showDivider: boolean;
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const tooltipProps = getTooltipContent(item);

  useEffect(() => {
    if (isTooltipOpen && buttonRef.current && tooltipRef.current) {
      const popperInstance = createPopper(
        buttonRef.current,
        tooltipRef.current,
        {
          placement: 'right-start',
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [-10, 10],
              },
            },
          ],
        },
      );

      return () => popperInstance.destroy();
    }
  }, [isTooltipOpen]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        !buttonRef.current?.contains(event.target as Node)
      ) {
        setIsTooltipOpen(false);
      }
    };

    const handleScroll = () => setIsTooltipOpen(false);

    if (isTooltipOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isTooltipOpen]);

  const handleCopy = () => {
    if (!tooltipProps.copy) return;
    navigator.clipboard.writeText(tooltipProps.copy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <Fragment key={item.created_date}>
      <div
        key={item.created_date}
        className="group relative flex flex-col gap-1 rounded p-2 hover:bg-background-inventory-recent-update-hover"
      >
        <div className="flex items-center justify-between gap-2.5">
          <div className="flex items-center gap-2">
            {recentUpdateItemIcon[item.type] ?? (
              <div className="h-4 w-4 rounded-full bg-[#94A3B8]" />
            )}
            <span className="text-sm font-bold text-content-base-default">
              {item.type}
            </span>
          </div>
          <span className="text-xs font-bold leading-5 text-content-base-default">
            Order # {item.details.location.order_number}
          </span>
        </div>

        <div className="flex items-start gap-2.5 self-stretch pl-[28px]">
          <div className="flex w-1/2 flex-col items-start gap-1">
            <p className="w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs font-semibold text-content-base-subdued">
              {item.details.location.address}
            </p>
            <p className="w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs font-semibold text-content-base-subdued">
              {[
                item.details.location.city,
                item.details.location.state,
                item.details.location.zip,
              ]
                .filter(Boolean)
                .join(', ')}
            </p>
          </div>

          <div className="relative flex w-1/2 flex-col items-end gap-1">
            <span className="text-center text-xs font-semibold text-content-base-subdued">
              {getFormattedUpdateTime(item.created_date)}
            </span>
            <div className="flex flex-col items-end gap-1 group-hover:hidden">
              <Tag
                key={item.created_date}
                content={{ type: 'label', label: item.product_category }}
                status="custom"
                className={clsx('bg-opacity-[8%]', {
                  'bg-[#9FF4A9] text-[#9FF4A9]':
                    item.product_category === 'Access',
                  'bg-[#6C9DFF] text-[#6C9DFF]':
                    item.product_category === 'Mobility',
                  'bg-[#8989FF] text-[#8989FF]':
                    item.product_category === 'Voice',
                  'bg-[#78CFDA] text-[#78CFDA]':
                    item.product_category === 'MNS',
                })}
              />
            </div>
            <div className="hidden w-full justify-end align-baseline group-hover:flex">
              <GraniteButton
                ref={buttonRef}
                variant="secondary"
                size="small"
                onClick={() => setIsTooltipOpen((prev) => !prev)}
              >
                View
              </GraniteButton>
            </div>
          </div>
        </div>
      </div>

      {showDivider ? <Divider className="w-full" /> : null}

      {isTooltipOpen &&
        createPortal(
          <div
            ref={tooltipRef}
            className="z-[9999] w-[334px] rounded-lg bg-background-base-surface-0 p-5 text-content-base-subdued shadow-elevation3"
          >
            <p className="mb-2 text-base font-bold text-content-base-default">
              {tooltipProps.type}
            </p>
            {tooltipProps.content}
            <div className="mt-4 flex gap-2">
              {tooltipProps.copy && (
                <GraniteButton size="small" onClick={handleCopy}>
                  {copied ? (
                    'Copied!'
                  ) : (
                    <>
                      Copy{' '}
                      <CopyOutline color="#191925" width="18px" height="18px" />
                    </>
                  )}
                </GraniteButton>
              )}
              {tooltipProps.download && (
                <GraniteButton size="small" onClick={tooltipProps.download}>
                  Download
                </GraniteButton>
              )}
            </div>
          </div>,
          document.body,
        )}
    </Fragment>
  );
};
export default UpdateItem;

function getFormattedUpdateTime(isoDateStr: string): string {
  const createdDate = parseISO(isoDateStr);
  const now = new Date();
  const diffInMs = now.getTime() - createdDate.getTime();
  const minutes = diffInMs / (1000 * 60);
  const hours = minutes / 60;

  if (minutes < 1) {
    return 'just now';
  } else if (hours < 1) {
    return `${Math.floor(minutes)} minutes ago`;
  } else if (hours < 24) {
    return `${Math.floor(hours)} hours ago`;
  } else {
    const time = format(createdDate, 'h:mm a');
    const date = format(createdDate, 'M/d/yy');
    return `${time} - ${date}`;
  }
}

const getTooltipContent = (item: RecentUpdateItem) => {
  const { type, metadata, attachedFiles, details } =
    item as RecentUpdateItem & {
      metadata?: Record<string, string>;
      attachedFiles?: { name: string; url: string }[];
    };

  const formatDate = (dateStr?: string) =>
    dateStr ? format(parseISO(dateStr), 'EEE, MMMM do, yyyy') : 'N/A';

  const renderList = (data: Record<string, string>) => (
    <ul className="list-inside list-disc text-sm">
      {Object.entries(data).map(([key, value]) => (
        <li key={key}>
          <span className="text-sm font-bold text-content-base-subdued">
            {key}:
          </span>{' '}
          <span className="text-sm font-medium text-content-base-subdued">
            {value}
          </span>
        </li>
      ))}
    </ul>
  );

  const downloadFiles = () => {
    attachedFiles?.forEach((file) => {
      const link = document.createElement('a');
      link.href = file.url;
      link.download = file.name;
      link.click();
    });
  };

  const getTooltipData = (): Record<string, string> => {
    switch (type) {
      case RecentItemStatus.ORDER_SHIPPED:
        return {
          'Tracking #': details?.tracking_id ?? 'N/A',
          'Expected delivery date': details?.expected_delivery_date
            ? formatDate(details?.expected_delivery_date)
            : 'N/A',
        };

      case RecentItemStatus.CIRCUIT_POSTED:
        return {
          Status: details?.circuit_install_status ?? 'N/A',
          'Site survey date': details?.site_survey_date
            ? formatDate(details?.site_survey_date)
            : 'N/A',
          'Install date': details.est_foc_or_scheduled_install_date
            ? formatDate(details?.est_foc_or_scheduled_install_date)
            : 'N/A',
          'Circuit ID': details?.lec_circuit_id ?? 'N/A',
          'IP address': details?.network_or_ip_address ?? 'N/A',
          'Usable IP range': details?.usable_ip_range ?? 'N/A',
          'Gateway IP': details?.gateway_or_monitored_ip ?? 'N/A',
          'Subnet mask': details?.subnet_mask ?? 'N/A',
          DNS1: details?.dns1 ?? 'N/A',
          DNS2: details?.dns2 ?? 'N/A',
          'Modem make/model': details?.modem_make_or_model ?? 'N/A',
          'Modem MAC': details?.broadcast_or_mac_address ?? 'N/A',
        };

      case RecentItemStatus.ORDER_CANCELED:
        return {
          'Cancellation reason': metadata?.cancellation_reason ?? 'N/A',
        };

      case RecentItemStatus.FOC_RECEIVED:
        return {
          'Firm Order Commitment (FOC) date': details?.foc_date
            ? formatDate(details?.foc_date)
            : 'N/A',
        };

      case RecentItemStatus.INSTALL_CONFIRMED:
        return {
          'Install date': details?.est_foc_or_scheduled_install_date
            ? formatDate(details?.est_foc_or_scheduled_install_date)
            : 'N/A',
        };

      default:
        return metadata ?? {};
    }
  };

  const tooltipData = getTooltipData();
  const lowerType = type?.toLowerCase() ?? '';

  return {
    type,
    content: renderList(tooltipData),
    copy: Object.entries(tooltipData)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n'),
    download: lowerType.includes('file attached') ? downloadFiles : undefined,
  };
};
